import type { FC, RefObject } from "react";
import { useRef, useState } from "react";

import { deepPurple, grey } from "@mui/material/colors";
import { Tooltip } from "@mui/material";

import { Box, Icons } from "@analyticsMD/quil";

import { css } from "@emotion/css";

import {
  UI_ACCENT_SUGGESTION_BG,
  UI_ACCENT_SUGGESTION_BORDER,
} from "../constants/ui-colors";
import IntelligenceText from "../types/IntelligenceText";
import SuggestionArasPopover from "./suggestion-aras-popover";

interface intelligenceTextProps {
  intelligenceText: IntelligenceText;
  /** When set, the denial / ARAS popover anchors below this element (e.g. wrapper around the field + intel row). */
  popoverAnchorRef?: RefObject<HTMLElement | null>;
}

const legacyStyles = {
  row: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1;
    min-width: 0;
    width: 100%;
    padding: 0.6rem 1rem;
    font-size: 0.875rem;
    text-align: left;
  `,
  text: css`
    flex: 1 1 0%;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
};

const denialBarStyles = {
  wrapper: css`
    display: block;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  `,
  bar: css`
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    background-color: ${UI_ACCENT_SUGGESTION_BG};
    padding: 0rem 0.75rem;
    border-radius: 0.4rem;
    border: 1px solid ${UI_ACCENT_SUGGESTION_BORDER};
    gap: 0.5rem;
    min-height: 2.5rem;
  `,
  content: css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
    flex: 1;
    font-size: 0.875rem;
    font-weight: 500;
    min-width: 0;
  `,
  trigger: css`
    display: inline-block;
    flex: 0 1 auto;
    width: fit-content;
    max-width: 100%;
    min-width: 0;
    color: ${grey[900]};
    font-weight: 400;
    cursor: pointer;
    border: none;
    border-bottom: 1px dotted ${grey[900]};
    text-decoration: none;
    font-size: 0.875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: transparent;
    padding: 0;
    font-family: inherit;
    text-align: left;
    vertical-align: middle;
  `,
};

const sxStyles = {
  legacyWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "left",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    backgroundColor: deepPurple[50],
    borderRadius: "0.4rem",
    border: `1px solid ${deepPurple[100]}`,
  },
};

const IntelligenceTextDisplay: FC<intelligenceTextProps> = ({
  intelligenceText,
  popoverAnchorRef,
}) => {
  const denialPrevention = intelligenceText.denial_prevention ?? null;
  const [arasOpen, setArasOpen] = useState(false);
  const fallbackAnchorRef = useRef<HTMLDivElement>(null);
  const arasAnchorEl =
    arasOpen
      ? popoverAnchorRef?.current ?? fallbackAnchorRef.current
      : null;

  if (denialPrevention != null) {
    return (
      <Box sx={{ width: "100%" }}>
        <div
          ref={popoverAnchorRef ? undefined : fallbackAnchorRef}
          className={denialBarStyles.wrapper}
        >
          <div className={denialBarStyles.bar}>
            <Icons.QBadgeIcon />
            <div className={denialBarStyles.content}>
              <button
                type="button"
                className={denialBarStyles.trigger}
                aria-expanded={arasOpen}
                aria-haspopup="dialog"
                title={intelligenceText.display_text}
                onClick={(e) => {
                  e.stopPropagation();
                  setArasOpen((open) => !open);
                }}
              >
                {intelligenceText.display_text}
              </button>
            </div>
          </div>
        </div>
        <SuggestionArasPopover
          open={arasOpen}
          anchorEl={arasAnchorEl}
          onClose={() => setArasOpen(false)}
          denialPrevention={denialPrevention}
        />
      </Box>
    );
  }

  return (
    <Tooltip title={intelligenceText.display_text} arrow placement="top">
      <Box sx={sxStyles.legacyWrapper}>
        <span className={legacyStyles.row}>
          <Icons.QBadgeIcon style={{ paddingRight: "0.4rem", flexShrink: 0 }} />
          <span className={legacyStyles.text}>
            {intelligenceText.display_text}
          </span>
        </span>
      </Box>
    </Tooltip>
  );
};

export default IntelligenceTextDisplay;
